import { Injectable, signal, computed } from '@angular/core'
import { HttpErrorResponse } from '@angular/common/http'
import {
  ExecutionField,
  ExecutionSearchParams,
} from '../models/execution-search.model'
import { Execution } from '../models/execution.entity'
import { ExecutionsService } from '../executions.service'
import { ExecutionsRepository } from '../executions.repository'
import {
  ExecutionsListingData,
  ExecutionsListingPage,
} from '../models/execution-listing.model'
import { Observable } from 'rxjs'
import { ActivitiesService } from '../../activities'
import { mergeById } from '../../../libs/filter.lib'

interface ExecutionsListingState {
  filters?: ExecutionSearchParams
  data?: Execution[]
  extData?: ExecutionsListingData
  dropdownOptions?: ExecutionsListingData
  totalCount?: number
  error?: HttpErrorResponse
  isLoading: boolean
  isInitialized: boolean
  fields?: ExecutionField[]
}

const EXECUTIONS_LISTING_INITIAL_STATE: ExecutionsListingState = {
  isLoading: false,
  isInitialized: false,
}

@Injectable()
export class ExecutionsListingStore {
  constructor(
    private executionsService: ExecutionsService,
    private executionsRepository: ExecutionsRepository,
    private activitiesService: ActivitiesService,
  ) {}

  private state = signal<ExecutionsListingState>(
    EXECUTIONS_LISTING_INITIAL_STATE,
  )

  // Selectors

  readonly isInitialized = computed(() => this.state().isInitialized)
  readonly isLoading = computed(() => this.state().isLoading)
  readonly filters = computed(() => this.state().filters)
  readonly isFiltered = computed(() => {
    const filters = this.state().filters
    return !!filters && Object.keys(filters).length > 0
  })
  readonly isErrored = computed(() => !!this.state().error)
  readonly executions = computed(() => this.state().data || [])
  readonly extData = computed(() => this.state().extData)
  readonly dropdownOptions = computed(() => this.state().dropdownOptions)
  readonly totalCount = computed(() => this.state().totalCount || 0)
  readonly fields = computed(() => this.state().fields)

  // Effects

  async search(
    params?: ExecutionSearchParams,
    returnAll?: boolean,
  ): Promise<void> {
    this.unsetError()

    return new Promise((resolve, reject) => {
      this.executionsRepository
        .searchExecutions$(
          this.parseStateFilterParams(params),
          this.state().fields ?? [],
          this.state().extData,
          returnAll,
        )
        .subscribe({
          next: (data) => {
            this.setData(data)
            resolve()
          },
          error: (error) => {
            this.setError(error)
            reject(error)
          },
        })
    })
  }

  async delete(execution: Execution): Promise<boolean> {
    return new Promise<boolean>((resolve) => {
      this.executionsRepository
        .alert('EXECUTION_DELETION', execution)
        .then((response) => {
          if (response === 'success') {
            this.setIsLoading(true)
            this.executionsService.delete$(execution._id).subscribe({
              next: () => {
                resolve(true)
                this.executionsRepository.notify({
                  code: 'EXECUTION_DELETED',
                })
              },
              error: (error: HttpErrorResponse) => {
                resolve(false)
                this.setError(error)
              },
            })
          }
        })
    })
  }

  async searchLookup(event: {
    dataSourceKey: string
    q?: string
    dataKeyField?: string
    dataDisplayField?: string
    offset?: number
    limit?: number
  }): Promise<void> {
    type LookupKey = keyof ExecutionsListingData
    type ArrayElement<T> = T extends (infer U)[] ? U : never
    type LookupValue = ArrayElement<
      NonNullable<ExecutionsListingData[LookupKey]>
    >

    const queryKey = (event.dataDisplayField ?? 'name') + ':ct'
    const offset = event.offset ?? 0
    const limit = event.limit ?? 50
    const query = {
      offset,
      limit,
      ...(event.q ? { [queryKey]: event.q } : {}),
    } as Record<string, unknown>

    const serviceMap: Partial<
      Record<LookupKey, () => Observable<LookupValue[]>>
    > = {
      activities: () => this.activitiesService.list$(query),
    }

    const search$ = serviceMap[event.dataSourceKey as LookupKey]
    if (!search$) return

    search$().subscribe((items) => {
      this.state.update((s) => {
        const key = event.dataSourceKey as LookupKey
        const prev = (s.dropdownOptions?.[key] ?? []) as { _id: string }[]
        const incoming = items as { _id: string }[]
        const merged = prev.length > 0 ? mergeById(prev, incoming) : incoming
        return {
          ...s,
          dropdownOptions: {
            ...s.extData,
            [event.dataSourceKey]: merged as LookupValue[],
          },
        }
      })
    })
  }

  // Reducers

  setData(data: ExecutionsListingPage) {
    const extData = this.extData()
    const newExtData = {
      activities: mergeById(extData?.activities, data.extData?.activities),
    }
    this.state.set({
      ...this.state(),
      data: data.data,
      totalCount: data.totalCount,
      extData: newExtData,
      dropdownOptions: newExtData,
      isInitialized: true,
      isLoading: false,
      error: undefined,
    })
  }

  setIsLoading(isLoading: boolean) {
    this.state.set({ ...this.state(), isLoading })
  }

  setFilters(filters?: ExecutionSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...filters },
    })
  }

  setError(error: HttpErrorResponse) {
    this.state.set({ ...this.state(), isLoading: false, error })
  }

  unsetError() {
    this.state.set({ ...this.state(), isLoading: true, error: undefined })
  }

  addFilter(filter: ExecutionSearchParams) {
    this.state.set({
      ...this.state(),
      isInitialized: true,
      filters: { ...this.state().filters, ...filter },
    })
  }

  resetLookups() {
    this.state.set({
      ...this.state(),
      dropdownOptions: undefined,
      extData: undefined,
    })
  }

  removeFilter(filter: ExecutionSearchParams) {
    const currentFilters = this.state().filters
    if (currentFilters) {
      // Create a new filters object without the specified filter properties
      const updatedFilters = { ...currentFilters }

      // Remove each property specified in the filter parameter
      for (const key of Object.keys(filter)) {
        if (key in updatedFilters) {
          delete updatedFilters[key as keyof ExecutionSearchParams]
        }
      }

      this.state.set({
        ...this.state(),
        isInitialized: true,
        filters: updatedFilters,
      })
    }
  }

  setFields(fields?: ExecutionField[]) {
    this.state.set({ ...this.state(), fields })
  }

  resetFilters() {
    this.state.set({
      ...this.state(),
      filters: {},
    })
  }

  private parseStateFilterParams(
    filters?: ExecutionSearchParams,
  ): ExecutionSearchParams {
    let params: ExecutionSearchParams = filters || {}
    params = Object.fromEntries(
      Object.entries(params).filter(
        ([key, value]) => key !== undefined && value !== undefined,
      ),
    )

    return params
  }
}
